import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

// Known validation failures already restore quantity and show a line error.
// Sibling components must not replace that message with error-free section HTML.
const handledCartValidationErrors = new WeakSet();

// Several cart components observe the same rejected operation. Read its current
// state once, without repeating the mutation or replacing app-owned fetch.
const cartReconciliations = new WeakMap();
let cartReconciliationId = 0;

function readCartAfterRejection(event) {
  const existing = cartReconciliations.get(event);
  if (existing) return existing;
  const components = [...document.querySelectorAll('cart-items-component')];
  const previousKeys = [...new Set(components.flatMap((component) =>
    [...component.querySelectorAll('[data-key]')].map((row) => row.dataset.key).filter(Boolean)
  ))];
  const sectionIds = [...new Set(components.map((component) => component.dataset.sectionId).filter(Boolean))];
  const controller = new AbortController();
  let timeout;
  const deadline = new Promise((_, reject) => {
    timeout = setTimeout(() => {
      controller.abort();
      reject(new Error('Cart reconciliation timed out'));
    }, 8000);
  });
  const read = async () => {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' }, credentials: 'same-origin',
      cache: 'no-store', signal: controller.signal,
    });
    if (!response.ok) throw new Error('Cart reconciliation unavailable');
    const cart = await response.json();
    if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count) ||
      cart.items.some((item) => typeof item.key !== 'string' || !item.key || !Number.isInteger(item.quantity))) {
      throw new Error('Invalid cart reconciliation');
    }
    const url = new URL(window.location.href);
    // Avoid an older in-flight section read as well as the renderer's HTML cache.
    url.searchParams.set('_cart_refresh', `${Date.now()}-${++cartReconciliationId}`);
    const sections = Object.fromEntries(await Promise.all(sectionIds.map(async (id) => {
      const html = await sectionRenderer.getSectionHTML(id, false, new URL(url), controller.signal);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const rows = [...parsed.querySelectorAll('[ref="cartItemRows[]"]')];
      const keys = rows.map((row) => row.dataset.key);
      const count = Number(parsed.querySelector('[ref="cartItemCount"]')?.textContent ?? 0);
      if (!parsed.getElementById(`shopify-section-${id}`) || keys.length !== cart.items.length ||
        count !== cart.item_count || cart.items.some((item) => {
          const row = rows.find((candidate) => candidate.dataset.key === item.key);
          const input = row?.querySelector('[ref="quantityInput"]');
          return !row || (input && Number(input.value) !== item.quantity);
        })) {
        throw new Error('Cart changed during reconciliation');
      }
      return [id, html];
    })));
    return { cart, sections, previousKeys };
  };
  const recovery = {
    promise: Promise.race([read(), deadline]).finally(() => {
      clearTimeout(timeout);
      controller.abort();
    }),
    notified: false,
  };
  cartReconciliations.set(event, recovery);
  return recovery;
}

function restoreCartControlFocus(control, dialog) {
  if (!control || control.isConnected || !dialog?.open || document.activeElement !== document.body) return;
  if ([...document.querySelectorAll('dialog:modal')].some((modal) => modal !== dialog)) return;
  const closeButton = dialog.querySelector('.theme-cart-empty__header button')
    ?? dialog.querySelector('.theme-drawer__close-button');
  closeButton?.focus({ preventScroll: true });
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {QuantitySelectorUpdateEvent | null} */
  #queuedQuantityChange = null;
  /** @param {Event} event */
  #queueQuantityChange = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent) ||
      !(event.target instanceof Node) || !this.contains(event.target) || this.#planChangePending) return;
    this.#queuedQuantityChange = event;
    this.#debouncedOnChange(event);
  };
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      this.#queuedQuantityChange = null;
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  #planChangePending = false;
  #cartRevision = 0;
  #externalCartUpdates = new Set();

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#debouncedOnChange.cancel();
    this.#queuedQuantityChange = null;

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (this.#planChangePending) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    if (this.#planChangePending) return;
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Keep the last row until the cart response renders the empty state.
    // Its purchase forms must not become reachable during a pending removal.
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;
    if (isEmptyCart) return;

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /** @param {Event} event */
  async onSellingPlanChange(event) {
    const control = event.target instanceof Element ? event.target.closest('[data-theme-cart-plan]') : null;
    if (!(control instanceof HTMLSelectElement || control instanceof HTMLButtonElement)) return;
    if (!this.isDrawer || !this.closest('.theme-cart-lines') || this.#planChangePending ||
      this.classList.contains('cart-items-disabled')) return;

    const row = control.closest('[data-key]');
    if (!(row instanceof HTMLElement) || !this.contains(row)) return;
    const plan = Number(control instanceof HTMLSelectElement ? control.value : control.dataset.planId);
    if (!Number.isSafeInteger(plan) || plan <= 0) return;

    const queuedQuantity = this.#queuedQuantityChange;
    this.#debouncedOnChange.cancel();
    this.#queuedQuantityChange = null;
    this.#planChangePending = true;
    this.inert = true;
    this.setAttribute('aria-busy', 'true');
    let updated = false;
    const resetControl = () => {
      if (control instanceof HTMLSelectElement) control.value = control.dataset.currentPlan ?? '';
    };
    try {
      // Finish the quantity debounce before reading the quantity for the plan mutation.
      if (queuedQuantity?.detail.cartLine) {
        const quantityUpdated = await this.updateQuantity({
          line: queuedQuantity.detail.cartLine,
          quantity: queuedQuantity.detail.quantity,
          action: 'change',
        });
        if (!quantityUpdated) {
          resetControl();
          return;
        }
      }
      await Promise.allSettled([...this.#externalCartUpdates]);
      const revision = this.#cartRevision;
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' }, cache: 'no-store',
      });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      const cartLine = cart.items.findIndex((item) => item.key === row.dataset.key);
      const line = this.refs.cartItemRows.findIndex((item) => item.dataset.key === row.dataset.key);
      if (revision !== this.#cartRevision || this.#externalCartUpdates.size || cartLine < 0 || line < 0) {
        throw new Error('Cart changed during plan selection');
      }
      updated = await this.updateQuantity({
        line: line + 1, cartLine: cartLine + 1, quantity: cart.items[cartLine].quantity,
        action: 'change', sellingPlan: plan,
      });
      if (!updated) resetControl();
    } catch {
      resetControl();
      const currentRow = this.refs.cartItemRows.find((item) => item.dataset.key === row.dataset.key);
      const error = currentRow?.querySelector('.cart-item__error-text');
      if (error) error.textContent = row.dataset.planError ?? '';
      currentRow?.querySelector('.cart-items__error')?.classList.remove('hidden');
      this.dispatchEvent(new CartErrorEvent({ error: row.dataset.planError ?? '', code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      this.#planChangePending = false;
      this.inert = false;
      this.removeAttribute('aria-busy');
      if (updated) {
        const updatedRow = this.refs.cartItemRows.find((item) => item.dataset.variantId === row.dataset.variantId &&
          item.querySelector(`[data-current-plan="${plan}"]`));
        updatedRow?.querySelector('[data-theme-cart-plan]')?.focus({ preventScroll: true });
      } else if (control.isConnected) {
        control.focus({ preventScroll: true });
      }
    }
  }

  /**
   * Updates the quantity or the native selling plan through the existing cart event path.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {number} [config.cartLine] - Fresh Ajax cart position for a plan change.
   * @param {number} [config.sellingPlan] - Existing native selling-plan ID.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const dialog = this.isDrawer ? this.closest('dialog') : null;
    const focusedCartControl = quantity === 0 && this.contains(document.activeElement)
      ? document.activeElement : null;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: config.cartLine ?? line,
      quantity: quantity,
      ...(config.sellingPlan !== undefined ? { selling_plan: config.sellingPlan } : {}),
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    const updateEvent = new CartLinesUpdateEvent({
      action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: lineId, quantity }],
      promise: deferredUpdatePromise.promise,
    });
    this.dispatchEvent(updateEvent);
    const revision = this.#cartRevision;

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors || parsedResponseText.status >= 400) {
          parsedResponseText.errors ||= parsedResponseText.description || parsedResponseText.message;
          this.#handleCartError(line, parsedResponseText);
          const validationError = new Error(parsedResponseText.errors);
          handledCartValidationErrors.add(validationError);
          deferredUpdatePromise.reject(validationError);
          return false;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
        restoreCartControlFocus(focusedCartControl, dialog);

        this.#updateCartQuantitySelectorButtonStates();
        return true;
      })
      .catch(async (error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        if (config.sellingPlan !== undefined) {
          const row = this.refs.cartItemRows[line - 1];
          const message = row?.querySelector('.cart-item__error-text');
          if (message) message.textContent = row.dataset.planError ?? '';
          row?.querySelector('.cart-items__error')?.classList.remove('hidden');
        }
        await this.#reconcileRejectedCart(updateEvent, error, revision, focusedCartControl);
        return false;
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    const revision = ++this.#cartRevision;
    if (event.target === this) return;
    const focusedCartControl = event.detail?.recoveryFocus ?? (event.action === 'add' && event.target instanceof Element &&
      event.target.matches('product-form-component') &&
      this.querySelector('[data-cart-drawer-empty]')?.contains(event.target) &&
      event.target.contains(document.activeElement) ? document.activeElement : null);
    if (event.promise) {
      this.#externalCartUpdates.add(event.promise);
      const clear = () => this.#externalCartUpdates.delete(event.promise);
      event.promise.then(clear, clear);
    }

    event.promise
      ?.then(async ({ detail }) => {
        if (event.detail?.reconciled && revision !== this.#cartRevision) return;
        // Keep the form's validation message when its confirmed native cart
        // agrees with the rendered rows. An error response can still leave a
        // changed cart, which needs the same read-only reconciliation.
        if (detail?.didError && event.target instanceof Element &&
          event.target.matches('product-form-component') && this.contains(event.target)) {
          const items = detail.source === 'product-form-component' && Array.isArray(detail.items) ? detail.items : null;
          const rows = this.refs.cartItemRows ?? [];
          const validItems = items?.every((item) => item && typeof item.key === 'string' && item.key &&
            Number.isInteger(item.quantity) && item.quantity >= 0);
          const changed = validItems && (items.length !== rows.length || items.some((item) => {
            const row = rows.find((candidate) => candidate.dataset.key === item.key);
            const input = row?.querySelector('[ref="quantityInput"]');
            return !row || (input && Number(input.value) !== item.quantity);
          }));
          if (changed) {
            const error = detail.error ?? event.target.querySelector('[ref="addToCartTextError"]')?.textContent?.trim();
            await this.#reconcileRejectedCart(event, error, revision, focusedCartControl);
          }
          return;
        }

        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(async () => {
              if (event.detail?.reconciled && revision !== this.#cartRevision) return;
              const dialog = this.closest('dialog');
              await morphSection(this.sectionId, cartItemsHtml, morphOptions);
              restoreCartControlFocus(focusedCartControl, dialog);
            }, ['fill-cart-drawer']);
          } else {
            const dialog = this.isDrawer ? this.closest('dialog') : null;
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
            restoreCartControlFocus(focusedCartControl, dialog);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch(async (error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
        if (!event.detail?.reconciled) await this.#reconcileRejectedCart(event, error, revision, focusedCartControl);
      });
  };

  async #reconcileRejectedCart(event, error, revision, recoveryFocus = null) {
    if (error?.name === 'AbortError' || handledCartValidationErrors.has(error) ||
      revision !== this.#cartRevision || !this.isConnected) return;
    try {
      const recovery = readCartAfterRejection(event);
      if (recoveryFocus) recovery.focus = recoveryFocus;
      const { cart, sections, previousKeys } = await recovery.promise;
      if (revision !== this.#cartRevision || !this.isConnected) return;
      resetShimmer(this);
      // Empty failed Adds have no real line key. Render the confirmed empty
      // section directly rather than inventing a line for a standard event.
      const lines = cart.items.length ? cart.items.map((item) => ({ id: item.key, quantity: item.quantity }))
        : previousKeys.map((id) => ({ id, quantity: 0 }));
      if (!lines.length) {
        const dialog = this.isDrawer ? this.closest('dialog') : null;
        await morphSection(this.sectionId, sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
          injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
        });
        restoreCartControlFocus(recovery.focus, dialog);
        this.#updateCartQuantitySelectorButtonStates();
        return;
      }
      if (recovery.notified) return;
      recovery.notified = true;
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update', context: 'cart', lines,
        detail: { reconciled: true, recoveryFocus: recovery.focus },
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { sections, items: cart.items, itemCount: cart.item_count,
            source: 'cart-reconciliation', didError: true, error },
        }),
      }));
    } catch (recoveryError) {
      if (recoveryError?.name !== 'AbortError') console.warn('[cart-items] Cart reconciliation unavailable:', recoveryError);
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
